import { Add } from "iconsax-react";
import { t } from "i18next";
import "./style.scss";
import {
    createContext,
    useContext,
    useEffect,
    useId,
    useRef,
    useState,
} from "react";

const ModalContext = createContext({ titleId: undefined });
let openModalCount = 0;
let previousBodyOverflow = "";

function DBModal({
    children,
    color,
    minWidth,
    blurred,
    show,
    onHide,
    permanent,
    className,
    stacked,
    ariaLabelledby,
    initialFocusRef,
}) {
    const [minWidthState, setMinWidthState] = useState(minWidth);
    const [display, setDisplay] = useState(false);
    const [render, setRender] = useState(false);
    const modalRef = useRef(null);
    const returnFocusRef = useRef(null);
    const onHideRef = useRef(onHide);
    const initialFocusRefBox = useRef(initialFocusRef);
    const generatedId = useId().replace(/:/g, "");
    const titleId = ariaLabelledby || `db-modal-title-${generatedId}`;
    onHideRef.current = onHide;
    initialFocusRefBox.current = initialFocusRef;

    useEffect(() => {
        const resizeHandle = () => {
            if (minWidth > window.innerWidth) {
                setMinWidthState("calc(100% - 32px)");
            } else {
                setMinWidthState(minWidth);
            }
        };
        resizeHandle();
        window.addEventListener("resize", resizeHandle, true);
        return () => window.removeEventListener("resize", resizeHandle, true);
    }, [minWidth]);

    useEffect(() => {
        let displayTimer;
        let renderTimer;
        if (show === true) {
            setRender(true);
            displayTimer = setTimeout(() => {
                setDisplay(true);
            }, 50);
        } else {
            setDisplay(false);
            renderTimer = setTimeout(() => {
                setRender(false);
            }, 300);
        }
        return () => {
            clearTimeout(displayTimer);
            clearTimeout(renderTimer);
        };
    }, [show]);

    useEffect(() => {
        if (!show) return undefined;
        returnFocusRef.current = document.activeElement;
        if (openModalCount === 0) {
            previousBodyOverflow = document.body.style.overflow;
            document.body.style.overflow = "hidden";
        }
        openModalCount += 1;
        const modalLayer = openModalCount;

        const focusableSelector = [
            "button:not([disabled])",
            "[href]",
            "input:not([disabled])",
            "select:not([disabled])",
            "textarea:not([disabled])",
            '[tabindex]:not([tabindex="-1"])',
        ].join(",");
        const focusInitial = window.setTimeout(() => {
            if (modalLayer !== openModalCount) return;
            const modal = modalRef.current;
            const preferred = initialFocusRefBox.current?.current;
            (preferred || modal?.querySelector("[autofocus]") ||
                modal?.querySelector(focusableSelector) ||
                modal)?.focus();
        }, 70);
        const handleKeyDown = (event) => {
            if (modalLayer !== openModalCount) return;
            const modal = modalRef.current;
            if (!modal) return;
            if (event.key === "Escape") {
                event.preventDefault();
                onHideRef.current?.();
                return;
            }
            if (event.key !== "Tab") return;
            const focusable = Array.from(
                modal.querySelectorAll(focusableSelector)
            ).filter((node) => node.offsetParent !== null);
            if (!focusable.length) {
                event.preventDefault();
                modal.focus();
                return;
            }
            const first = focusable[0];
            const last = focusable[focusable.length - 1];
            if (event.shiftKey && document.activeElement === first) {
                event.preventDefault();
                last.focus();
            } else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault();
                first.focus();
            }
        };
        document.addEventListener("keydown", handleKeyDown);
        return () => {
            window.clearTimeout(focusInitial);
            document.removeEventListener("keydown", handleKeyDown);
            openModalCount = Math.max(0, openModalCount - 1);
            if (openModalCount === 0) {
                document.body.style.overflow = previousBodyOverflow;
            }
            const returnTarget = returnFocusRef.current;
            if (returnTarget && document.contains(returnTarget)) {
                window.setTimeout(() => returnTarget.focus(), 0);
            }
        };
    }, [show]);

    return (
        <>
            {render && (
                <>
                    <div
                        ref={modalRef}
                        className={`db-modal ${display ? "show" : ""} ${
                            className || ""
                        } ${stacked ? "db-modal-stacked" : ""}`}
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby={titleId}
                        tabIndex={-1}
                        style={{
                            backgroundColor: color ? color : "white",
                            width: minWidthState,
                        }}
                    >
                        <ModalContext.Provider value={{ titleId }}>
                            {children}
                        </ModalContext.Provider>
                    </div>
                    <div
                        onClick={() => !permanent && onHide()}
                        className={`db-modal-backdrop ${display ? "show" : ""} ${
                            stacked ? "db-modal-backdrop-stacked" : ""
                        }`}
                        style={{
                            backdropFilter: blurred ? "blur(10px)" : null,
                        }}
                    ></div>
                </>
            )}
        </>
    );
}

function Content({ children }) {
    return <div className="db-modal-content">{children}</div>;
}

function Header({ icon, title, children, closeBtn, closeLabel }) {
    const { titleId } = useContext(ModalContext);
    return (
        <div className="db-modal-header">
            <div className="icon">{icon && <img src={icon} alt="" />} </div>
            <div className="title" id={titleId}>
                {title || t("dialog")}
            </div>
            <div className="children">{children}</div>
            <div>
                {closeBtn && (
                    <button
                        type="button"
                        onClick={closeBtn}
                        aria-label={closeLabel || t("cancel")}
                    >
                        <Add size={24} color="#333" aria-hidden="true" />
                    </button>
                )}
            </div>
        </div>
    );
}

function Footer({ children }) {
    return <div className="db-modal-footer">{children}</div>;
}

function Tab({ tabs, selected, onChange, color, selectedColor }) {
    const tabRefs = useRef([]);
    const handleKeyDown = (event, index) => {
        if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) {
            return;
        }
        event.preventDefault();
        const rtl = document.documentElement.dir === "rtl";
        let next = index;
        if (event.key === "Home") next = 0;
        if (event.key === "End") next = tabs.length - 1;
        if (event.key === "ArrowRight") next += rtl ? -1 : 1;
        if (event.key === "ArrowLeft") next += rtl ? 1 : -1;
        next = (next + tabs.length) % tabs.length;
        onChange(next);
        tabRefs.current[next]?.focus();
    };
    return (
        <div className="db-modal-tabs" role="tablist">
            <div style={{ color }}>
                {tabs.map((tab, index) => (
                    <button
                        key={index}
                        type="button"
                        role="tab"
                        aria-selected={selected === index}
                        tabIndex={selected === index ? 0 : -1}
                        ref={(node) => {
                            tabRefs.current[index] = node;
                        }}
                        onClick={() => onChange(index)}
                        onKeyDown={(event) => handleKeyDown(event, index)}
                        className={selected === index ? "selected" : ""}
                        style={{
                            borderColor:
                                selected === index &&
                                (selectedColor ? selectedColor : color),
                            color:
                                selected === index && selectedColor
                                    ? selectedColor
                                    : null,
                        }}
                    >
                        {tab}
                    </button>
                ))}
            </div>
        </div>
    );
}

DBModal.Content = Content;
DBModal.Header = Header;
DBModal.Tab = Tab;
DBModal.Footer = Footer;

export default DBModal;
